/* eslint-disable @typescript-eslint/consistent-type-definitions -- Zero mutator/query arg types must be `type` aliases to satisfy the ReadonlyJSONValue constraint; interfaces lack an implicit index signature. */
// ── Condition field identifiers ──────────────────────────────────────────────

export type SlaConditionField =
	| "thread.painLevel"
	| "thread.origin"
	| "thread.tags"
	| "company.name"
	| "company.size"
	| "company.revenue"
	| "company.tierName"
	| "company.statusName"
	| "contact.email"

// ── Operators ────────────────────────────────────────────────────────────────

export type SlaConditionOperator =
	| "eq"
	| "neq"
	| "gt"
	| "lt"
	| "gte"
	| "lte"
	| "contains"
	| "startsWith"
	| "endsWith"

// ── Condition types ──────────────────────────────────────────────────────────

export type SlaCondition = {
	field: SlaConditionField
	operator: SlaConditionOperator
	value: string | number | null
}

export type SlaConditionGroup = {
	match: "all" | "any" // "all" = AND, "any" = OR
	conditions: SlaCondition[]
}

// ── Evaluation context ───────────────────────────────────────────────────────

export interface SlaEvaluationContext {
	thread: {
		painLevel: string
		origin: string
		tagIds?: string[]
	}
	company: {
		name: string | null
		size: number | null
		revenue: number | null
		tierName: string | null
		statusName: string | null
	} | null
	contact: {
		email: string | null
	} | null
}

// ── Field metadata (for the UI condition builder) ────────────────────────────

export type ConditionFieldType = "string" | "number" | "enum" | "tag"

export interface ConditionFieldMeta {
	label: string
	type: ConditionFieldType
	group: "Thread" | "Company" | "Contact"
	operators: SlaConditionOperator[]
	operatorLabels?: Partial<Record<SlaConditionOperator, string>>
	options?: { value: string; label: string }[]
}

const STRING_OPS: SlaConditionOperator[] = [
	"eq",
	"neq",
	"contains",
	"startsWith",
	"endsWith",
]
const NUMBER_OPS: SlaConditionOperator[] = [
	"eq",
	"neq",
	"gt",
	"lt",
	"gte",
	"lte",
]
const ENUM_OPS: SlaConditionOperator[] = ["eq", "neq"]

export const SLA_CONDITION_FIELDS: Record<
	SlaConditionField,
	ConditionFieldMeta
> = {
	"thread.painLevel": {
		label: "Priority",
		type: "enum",
		group: "Thread",
		operators: ENUM_OPS,
		options: [
			{ value: "HIGH", label: "High" },
			{ value: "MEDIUM", label: "Medium" },
			{ value: "LOW", label: "Low" },
			{ value: "UNKNOWN", label: "None" },
		],
	},
	"thread.origin": {
		label: "Origin",
		type: "enum",
		group: "Thread",
		operators: ENUM_OPS,
		options: [
			{ value: "WIDGET", label: "Widget" },
			{ value: "SUPPORT_PORTAL", label: "Support Portal" },
			{ value: "PORTAL", label: "Portal" },
			{ value: "SLACK_CONNECT", label: "Slack Connect" },
			{ value: "EMAIL", label: "Email" },
			{ value: "LIVE_CHAT", label: "Live Chat" },
			{ value: "ZAPIER", label: "Zapier" },
			{ value: "API", label: "API" },
			{ value: "INTERCOM", label: "Intercom" },
			{ value: "ZENDESK", label: "Zendesk" },
			{ value: "PLAIN", label: "Plain" },
		],
	},
	"thread.tags": {
		label: "Tag",
		type: "tag",
		group: "Thread",
		operators: ENUM_OPS,
		operatorLabels: { eq: "has", neq: "does not have" },
	},
	"company.name": {
		label: "Company Name",
		type: "string",
		group: "Company",
		operators: STRING_OPS,
	},
	"company.size": {
		label: "Company Size",
		type: "number",
		group: "Company",
		operators: NUMBER_OPS,
	},
	"company.revenue": {
		label: "Company Revenue",
		type: "number",
		group: "Company",
		operators: NUMBER_OPS,
	},
	"company.tierName": {
		label: "Company Tier",
		type: "string",
		group: "Company",
		operators: STRING_OPS,
	},
	"company.statusName": {
		label: "Company Status",
		type: "string",
		group: "Company",
		operators: STRING_OPS,
	},
	"contact.email": {
		label: "Contact Email",
		type: "string",
		group: "Contact",
		operators: STRING_OPS,
	},
}

export const OPERATOR_LABELS: Record<SlaConditionOperator, string> = {
	eq: "equals",
	neq: "does not equal",
	gt: "greater than",
	lt: "less than",
	gte: "greater than or equal",
	lte: "less than or equal",
	contains: "contains",
	startsWith: "starts with",
	endsWith: "ends with",
}

// ── Evaluation engine ────────────────────────────────────────────────────────

export function evaluateConditionGroup(
	group: SlaConditionGroup,
	ctx: SlaEvaluationContext,
): boolean {
	if (group.conditions.length === 0) return true

	if (group.match === "all") {
		return group.conditions.every((c) => evaluateCondition(c, ctx))
	}
	return group.conditions.some((c) => evaluateCondition(c, ctx))
}

function evaluateCondition(
	condition: SlaCondition,
	ctx: SlaEvaluationContext,
): boolean {
	if (condition.field === "thread.tags") {
		const hasTag = (ctx.thread.tagIds ?? []).includes(String(condition.value))
		return condition.operator === "neq" ? !hasTag : hasTag
	}
	const actual = resolveField(condition.field, ctx)
	return compareValues(actual, condition.operator, condition.value)
}

function resolveField(
	field: SlaConditionField,
	ctx: SlaEvaluationContext,
): string | number | null {
	switch (field) {
		case "thread.painLevel":
			return ctx.thread.painLevel
		case "thread.origin":
			return ctx.thread.origin
		case "thread.tags":
			return null
		case "company.name":
			return ctx.company?.name ?? null
		case "company.size":
			return ctx.company?.size ?? null
		case "company.revenue":
			return ctx.company?.revenue ?? null
		case "company.tierName":
			return ctx.company?.tierName ?? null
		case "company.statusName":
			return ctx.company?.statusName ?? null
		case "contact.email":
			return ctx.contact?.email ?? null
	}
}

function compareValues(
	actual: string | number | null,
	operator: SlaConditionOperator,
	expected: string | number | null,
): boolean {
	if (actual == null) return false

	switch (operator) {
		case "eq":
			return actual === expected
		case "neq":
			return actual !== expected
		case "gt":
			return (
				typeof actual === "number" &&
				typeof expected === "number" &&
				actual > expected
			)
		case "lt":
			return (
				typeof actual === "number" &&
				typeof expected === "number" &&
				actual < expected
			)
		case "gte":
			return (
				typeof actual === "number" &&
				typeof expected === "number" &&
				actual >= expected
			)
		case "lte":
			return (
				typeof actual === "number" &&
				typeof expected === "number" &&
				actual <= expected
			)
		case "contains":
			return (
				typeof actual === "string" &&
				typeof expected === "string" &&
				actual.toLowerCase().includes(expected.toLowerCase())
			)
		case "startsWith":
			return (
				typeof actual === "string" &&
				typeof expected === "string" &&
				actual.toLowerCase().startsWith(expected.toLowerCase())
			)
		case "endsWith":
			return (
				typeof actual === "string" &&
				typeof expected === "string" &&
				actual.toLowerCase().endsWith(expected.toLowerCase())
			)
	}
}
